import React, {useState} from 'react';
import {Select, Button, message} from 'antd';
import {useMutation, useQuery} from '@apollo/client';

import {getUserInfo} from "../../../utils/auth";
import {useHashState} from 'utils/hashState';
import {ParcelInitialValue} from "../../../config/filterOptions";
import {getLastWeekDateRange} from "../../../utils/dateTimeUtils";
import {OrderWithAddressType} from "./hooks/useWmsExceptionQuery";
import CentralParcelDetailDrawer from "../../central/parcels/components/CentralParcelDetailDrawer";
import {useWmsExceptionQuery} from "./hooks/useWmsExceptionQuery";
import WmsExceptionFilter from "./components/WmsExceptionFilter";
import {WmsExceptionTable} from "./components/WmsExceptionTable";
import TablePaginationBar from '../../../components/TablePaginationBar';
import {BATCH_SET_NEXT_ACTION,BatchSetNextActionResponse,BatchSetNextActionVariables} from "./graphql/mutations/setNextAction";
import { MARK_AS_EXCEPTION } from '../../../graphql/mutations/markAsException';
import BatchMarkAsExceptionModal, { BatchMarkExceptionInput } from 'components/BatchMarkAsExceptionModal';
import { GET_ALL_EXCEPTION, ExceptionDictionaryType } from '../../delivery/parcels/graphql/queries/getAllException';
import { filterExceptionByUserRole } from 'config/roleExceptionConfig';
import { GET_NEXT_ACTION_LIST, NextActionListResponse, NextActionListType } from '../../../graphql/queries/getNextActionList';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import {SiteType} from "../../../graphql/types/SiteType";
import { useTranslation } from 'react-i18next';
import { useStatusInternationalization } from '../../delivery/parcels/hooks/useStatusInternationalization';
import { formatTimeRangeToISO } from "../../../utils/dateTimeUtils";
import { showError } from '../../../utils/exceptionUtils';
import {
    GENERATE_EXCEPTION_DOWNLOAD_REPORT_FOR_WMS,
    GenerateExceptionDownloadReportForWmsResponse,
    GenerateExceptionDownloadReportForWmsVariables
} from "./graphql/mutations/generateExceptionDownloadReportForWms";
import ParcelsDownloadModal from "../../central/parcels/components/ParcelsDownloadModal";
import { useDownloadCooldown } from "../../../hooks/useDownloadCooldown";

const WmsExceptionPage: React.FC = () => {
    const { currentWarehouse } = useWarehouseContext();
    const { t } = useTranslation();
    const { getNextActionTextFromBackend} = useStatusInternationalization();
    const [detailVisible, setDetailVisible] = useState(false);
    const [orderId, setOrderId] = useState<string>('');

    // 异常页面专用的初始筛选条件 - 默认显示所有曾经被标记过异常的订单
    // 默认异常时间为最近一周
    const exceptionInitialValue = {
        ...ParcelInitialValue,
        isHasBeenSetException: true,  // 默认显示所有曾经被标记过异常的订单
        // 默认日期不写入 URL/hash：由页面/筛选组件在运行时展示"最近一周"
        markExceptionAt: undefined
    };

    // - undefined：未初始化，默认最近一周
    // - null：用户手动清空（点击叉号），保持为空
    const getEffectiveMarkExceptionAt = (value: any): [string | null, string | null] | null => {
        if (value === null) return null;
        const isValid = value &&
            Array.isArray(value) &&
            value.length === 2 &&
            value[0] !== null &&
            value[1] !== null;
        return isValid ? (value as [string | null, string | null]) : getLastWeekDateRange();
    };

    const [tempFilters, setTempFilters] = useHashState(exceptionInitialValue);
    const [filters, setFilters] = useHashState(exceptionInitialValue);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<OrderWithAddressType[]>([]);
    const [actionName, setActionName] = useState<string | undefined>(undefined);
    const [batchModalVisible, setBatchModalVisible] = useState(false);
    const [batchMarkLoading, setBatchMarkLoading] = useState(false);

    // 异步下载相关状态
    const { downloadLoading, setDownloadLoading, cooldownRemaining, getLastDownloadTime, setLastDownloadTime } = useDownloadCooldown(`exceptionDownloadLastTime_${getUserInfo()?.account?.id}`);
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState<string>('');
    const [generateReportMutation] = useMutation<GenerateExceptionDownloadReportForWmsResponse, GenerateExceptionDownloadReportForWmsVariables>(GENERATE_EXCEPTION_DOWNLOAD_REPORT_FOR_WMS);

    // GraphQL Mutations
    const [batchSetNextAction, { loading: setBatchActionLoading }] = useMutation<BatchSetNextActionResponse, BatchSetNextActionVariables>(BATCH_SET_NEXT_ACTION);
    const [markAsException] = useMutation(MARK_AS_EXCEPTION);

    // 使用WMS异常专用查询Hook，包含地址信息
    const {
        parcelData,
        loading,
        refetch,
        totalElements,
        buildQueryVariables
    } = useWmsExceptionQuery(
        {
            ...filters,
            markExceptionAt: getEffectiveMarkExceptionAt(filters.markExceptionAt)
        } as any,
        currentPage,
        pageSize
    );

    const userRole = getUserInfo()?.account?.role?.name || '';
    const { data: exceptionData } = useQuery(GET_ALL_EXCEPTION);
    const { data: nextActionData } = useQuery<NextActionListResponse>(GET_NEXT_ACTION_LIST);
    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters || [];
    const exceptionSelectData: ExceptionDictionaryType[] = filterExceptionByUserRole(exceptionList, userRole, currentWarehouse?.siteType);
    const nextActionSelectData: NextActionListType[] = nextActionData?.getNextActionList || [];

    // 获取仓库信息进行数据隔离
    const siteType = currentWarehouse?.siteType;
    const sortingCodes = currentWarehouse?.sortingCode?.split('-') || [];

    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: OrderWithAddressType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };

    const handleResetFilters = () => {
        setTempFilters(exceptionInitialValue);
        setFilters(exceptionInitialValue);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    const showParcelDetail = (record: OrderWithAddressType) => {
        setOrderId(record.id);
        setDetailVisible(true);
    };

    // 处理分页变化
    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    // 批量设置Next Action
    const handleBatchSet = async () => {
        if (!actionName || selectedRows.length === 0) {
            message.warning(t('wms.exceptions.messages.pleaseSelectOrdersAndAction'));
            return;
        }

        // 只处理异常状态的订单（isAbnormal = true）
        const abnormalOrders = selectedRows.filter(order => order.isAbnormal);

        if (abnormalOrders.length === 0) {
            message.warning(t('wms.exceptions.messages.onlyOrdersWithOpenStatusCanBeUpdated'));
            return;
        }

        try {
            // 使用批量接口
            const orderIds = abnormalOrders.map(order => parseInt(order.id));

            const result = await batchSetNextAction({
                variables: {
                    orderIds: orderIds,
                    actionName: actionName
                },
                // 确保缓存更新
                refetchQueries: ['WarehouseQuery']
            });

            const updatedCount = result.data?.batchSetNextAction.length || 0;

            message.success(t('wms.exceptions.messages.successfullyUpdatedOrdersToAction', {
                count: updatedCount,
                action: actionName
            }));

            // 清空选择和批量操作
            setSelectedRowKeys([]);
            setSelectedRows([]);
            setActionName(undefined);

            // 刷新数据
            refetch(buildQueryVariables());

        } catch (error) {
            console.error('Batch update failed:', error);
            showError(error);
        }
    };

    // 异步下载处理函数
    const handleDownloadExceptions = async () => {
        const currentTime = Date.now();
        const lastTime = getLastDownloadTime();

        if (currentTime - lastTime < 5000) {
            const remainingTime = Math.ceil((5000 - (currentTime - lastTime)) / 1000);
            message.warning(`Please wait ${remainingTime} seconds before trying again`);
            return;
        }
        if (downloadLoading) {
            message.warning('Report is being generated, please do not click repeatedly');
            return;
        }

        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime);

            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            // 构建查询参数（与列表查询保持一致的数据隔离逻辑）
            const sortingFilters: Record<string, string | undefined> = {};
            if (sortingCodes.length > 0) {
                sortingFilters.sortingCodeSegment1 = sortingCodes[0] || undefined;
                if (sortingCodes.length > 1) {
                    sortingFilters.sortingCodeSegment2 = sortingCodes[1] || undefined;
                    if (sortingCodes.length > 2) {
                        sortingFilters.sortingCodeSegment3 = sortingCodes[2] || undefined;
                    }
                }
            }

            const queryFilters: any = {
                trackingNumbers: filters.trackingNumbers || undefined,
                channelId: filters.channelId?.[0] ? parseInt(filters.channelId[0]) : undefined,
                overallStatuses: filters.overallStatus && filters.overallStatus.length > 0 ? filters.overallStatus : undefined,
                exceptionReasonCode: filters.exceptionReasonCode || undefined,
                exceptionReasonCodes: filters.exceptionReasonCodes || undefined,
                markExceptionAt: formatTimeRangeToISO(getEffectiveMarkExceptionAt(filters.markExceptionAt)),
                isAbnormal: filters.isAbnormal,
                exceptionStatuses: filters.exceptionStatuses || undefined,
                nextAction: filters.nextAction || undefined,
                exceptionHubId: String(siteType) === String(SiteType.HUB) ? currentWarehouse?.id : currentWarehouse?.parentSiteId,
                exceptionWarehouseId: filters.exceptionWarehouseId ? filters.exceptionWarehouseId : (String(siteType) === String(SiteType.WAREHOUSE) ? currentWarehouse?.id : undefined),
                isHasBeenSetException: true,
                ...sortingFilters
            };

            // 勾选下载：如果有选中行，用 trackingNumbers 覆盖
            if (selectedRows && selectedRows.length > 0) {
                const trackingNumbers = selectedRows
                    .map(row => row.trackingNumber)
                    .filter((tn): tn is string => !!tn);
                if (trackingNumbers.length > 0) {
                    queryFilters.trackingNumbers = trackingNumbers.join(';');
                }
            }

            const response = await generateReportMutation({
                variables: {
                    filters: queryFilters,
                    timezone: userTimezone,
                    warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : undefined
                }
            });

            const fileName = response.data?.generateExceptionDownloadReportForWms;
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadCompleteModalVisible(true);
            } else {
                message.error('Failed to start report generation task');
            }
        } catch (error: any) {
            console.error('Report generation failed:', error);
            showError(error);
            setDownloadLoading(false);
        }
    };

    // 批量异常弹窗确认
    const handleBatchMarkOk = async (orders: BatchMarkExceptionInput[]) => {
        setBatchMarkLoading(true);
        try {
            await Promise.all(orders.map(order =>
                markAsException({
                    variables: {
                        orderId: order.orderId,
                        exceptionReasonCode: order.exceptionReasonCode,
                        exceptionHubId: siteType == SiteType.HUB ? currentWarehouse?.id : currentWarehouse?.parentSiteId,
                        exceptionWarehouseId: siteType == SiteType.WAREHOUSE ? currentWarehouse?.id : null,
                        exceptionImageS3KeysJson: order.exceptionImageS3KeysJson || []
                    },
                    refetchQueries: ['WarehouseQuery']
                })
            ));
            message.success(t('wms.exceptions.messages.batchMarkAsExceptionSuccess'));
            setBatchModalVisible(false);
            setSelectedRowKeys([]);
            setSelectedRows([]);
            refetch(buildQueryVariables());
        } catch (e) {
            message.error(t('wms.exceptions.messages.batchMarkAsExceptionFailed'));
        } finally {
            setBatchMarkLoading(false);
        }
    };

    return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">

                {/*  异常专用筛选器 */}
                <WmsExceptionFilter
                    filters={tempFilters}
                    setFilters={setTempFilters}
                    handleFilter={handleFilter}
                    handleResetFilters={handleResetFilters}
                    selectedRows={selectedRows}
                />

                {/*  批量操作区域 */}
                <div className="flex justify-between items-center mb-3 pl-0">
                    {/* 左侧 - 功能按钮 */}
                    <div className="flex items-center gap-2">
                        <Button
                            type="primary"
                            onClick={() => {
                                if (selectedRows.length === 0) {
                                    message.warning(t('wms.exceptions.messages.selectAtLeastOneOrder'));
                                    return;
                                }
                                setBatchModalVisible(true);
                            }}
                            disabled={selectedRowKeys.length === 0}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('wms.exceptions.buttons.markAsException')}</span>
                        </Button>
                        <span className="mr-2 text-gray-800 font-semibold">{t('wms.exceptions.filters.nextAction')}</span>
                        <Select
                            placeholder={t('components.input.select')}
                            value={actionName}
                            onChange={setActionName}
                            style={{ width: 140, marginRight: 8 }}
                            options={nextActionSelectData.filter(item => item.value !== 0 && item.name !== 'Compensate').map(option => ({
                                value: option.name,
                                label: getNextActionTextFromBackend(option.name)
                            }))}
                            disabled={selectedRowKeys.length === 0}
                        />
                        <Button
                            type="primary"
                            onClick={handleBatchSet}
                            loading={setBatchActionLoading}
                            disabled={selectedRowKeys.length === 0 || !actionName}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('wms.exceptions.buttons.set')}</span>
                        </Button>
                    </div>

                    {/* 右侧 - 筛选相关按钮 */}
                    <div className="flex items-center gap-2">
                        <Button
                            type="primary"
                            onClick={handleDownloadExceptions}
                            loading={cooldownRemaining > 0}
                            disabled={downloadLoading || cooldownRemaining > 0}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">
                                {cooldownRemaining > 0
                                    ? `${t('wms.exceptions.buttons.download')} ${cooldownRemaining}s`
                                    : t('wms.exceptions.buttons.download')
                                }
                            </span>
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleFilter}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('wms.exceptions.filters.filter')}</span>
                        </Button>
                        <Button
                            onClick={handleResetFilters}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('wms.exceptions.filters.reset')}</span>
                        </Button>
                    </div>
                </div>

                {/* 异常专用表格 */}
                <WmsExceptionTable
                    parcelData={parcelData}
                    showParcelDetail={showParcelDetail}
                    onSelectChange={handleSelectChange}
                    selectedRowKeys={selectedRowKeys}
                    loading={loading}
                    onRefresh={() => refetch(buildQueryVariables())}
                />

                {/* 分页 */}
                <TablePaginationBar
                    total={totalElements}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                />

                {/* 包裹详情抽屉组件 */}
                <CentralParcelDetailDrawer
                    showInterceptionExemptionAlert={true}
                    visible={detailVisible}
                    onClose={() => setDetailVisible(false)}
                    orderId={orderId}
                    userRole={getUserInfo()?.account?.role?.name || ''}
                />
                {/* 批量标记异常弹窗 */}
                <BatchMarkAsExceptionModal
                    visible={batchModalVisible}
                    parcels={selectedRows.map(row => ({
                        orderId: Number(row.id),
                        trackingNumber: row.trackingNumber,
                        existingImageUrls: row.exceptionImageS3UrlsJson || []
                    }))}
                    onOk={handleBatchMarkOk}
                    onCancel={() => setBatchModalVisible(false)}
                    loading={batchMarkLoading}
                    userRole={userRole}
                    exceptionSelectData={exceptionSelectData}
                />
                {/* 下载完成提示弹窗 */}
                <ParcelsDownloadModal
                    visible={downloadCompleteModalVisible}
                    onClose={() => setDownloadCompleteModalVisible(false)}
                    fileName={generatedFileName}
                />
            </div>
    );
};

export default WmsExceptionPage;